import { useEffect, useRef, useState } from 'react';
import {
  useColorTheme,
  useQueryParams,
  useGetDisplayedPlan,
  useSubscriptionPlanById,
  usePaymentControlState,
  useGetUser,
} from 'src/hooks';
import styles from './CheckoutSection.module.scss';
import { Elements } from '@stripe/react-stripe-js';
import { AppearingFromRight } from 'src/animations/AppearingFromRight';
import { getReferralId, initializeGTMDeferred, stripePromise } from 'src/utils';
import { CheckoutSeatsSection } from 'src/v2/pages/payment/components/CheckoutSeatsSection';
import { CheckoutCouponSection } from 'src/v2/pages/payment/components/CheckoutCouponSection';
import { PlanFamily } from 'src/types/api';
import { PriceDiscountType } from 'src/types/wallet/models/PriceDiscountType';
import { PanelHeader } from 'src/pages/PaymentBetaPage/components/PanelHeader';
import { SummarySection } from 'src/pages/PaymentBetaPage/components/SummarySection';
import { PayPalElements } from 'src/pages/PaymentBetaPage/components/PayPalElements';
import { StripeElements } from 'src/pages/PaymentBetaPage/components/StripeElements';

type CheckoutSectionProps = {
  isOpen: boolean;
  onClose: () => void;
  onPaymentProcessing: (value: boolean) => void;
  appliedCoupon?: string;
  onChangeAppliedCoupon: (value: string | undefined) => void;
};

export const CheckoutSection = ({
  isOpen,
  onClose,
  onPaymentProcessing,
  appliedCoupon,
  onChangeAppliedCoupon,
}: CheckoutSectionProps) => {
  const { searchParams } = useQueryParams();
  const { isDarkTheme } = useColorTheme();
  const { isEducationUser } = useGetUser();
  const checkoutContentRef = useRef<HTMLDivElement>(null);
  const [numberOfSeats, setNumberOfSeats] = useState(1);
  const referralId = getReferralId();
  const enablePaypal = true;

  const { selectedFamily } = usePaymentControlState();
  const { currentSelectedPlanV2: currentSelectedPlan } = useGetDisplayedPlan();

  const [isReadyStripeElements, setReadyStripeElements] =
    useState<boolean>(false);
  const [isReadyPaypalElements, setReadyPaypalElements] =
    useState<boolean>(false);

  const isTeams = selectedFamily === PlanFamily.BUSINESS;

  const { plan, isPlanLoading } = useSubscriptionPlanById({
    plan_id: currentSelectedPlan?.plan_id,
    seat_count: isTeams ? numberOfSeats : undefined,
    stripe_promotion_code:
      isEducationUser && !isTeams ? undefined : appliedCoupon,
    discountType: isEducationUser
      ? PriceDiscountType.STUDENT_DISCOUNT
      : undefined,
    withoutReferral: isTeams,
  });

  useEffect(() => {
    if (isReadyStripeElements && isReadyPaypalElements) {
      initializeGTMDeferred();
    }
  }, [isReadyStripeElements, isReadyPaypalElements]);

  useEffect(() => {
    if (!!searchParams.coupon) {
      onChangeAppliedCoupon(searchParams.coupon);
    }
  }, [searchParams.coupon, onChangeAppliedCoupon]);

  const handleResetState = () => {
    onChangeAppliedCoupon(undefined);
  };

  const handlePaymentProcessingChange = (value: boolean) => {
    onPaymentProcessing(value);
  };

  const handleChangeNumberOfSeats = (number: number) => {
    if (number <= 0) {
      setNumberOfSeats(1);
    } else {
      setNumberOfSeats(number);
    }
  };

  const appliedCouponHasError =
    !!appliedCoupon && !plan?.metadata?.stripe?.coupon_metadata;

  return (
    <AppearingFromRight
      inProp={isOpen}
      nodeRef={checkoutContentRef}
      onExited={handleResetState}
      width={360}
      enableTabletMode={false}
    >
      <div className={styles.root}>
        <PanelHeader onClose={onClose} />

        <div className={styles.mainContainer}>
          {isTeams && (
            <CheckoutSeatsSection
              numberOfSeats={numberOfSeats}
              onChangeNumberOfSeats={handleChangeNumberOfSeats}
            />
          )}
          <SummarySection
            currentPlan={plan}
            isTeams={isTeams}
            isLoading={isPlanLoading}
            coupon={appliedCoupon}
          />

          {!referralId && !(isEducationUser && !isTeams) && (
            <CheckoutCouponSection
              hasError={appliedCouponHasError}
              onChangeAppliedValue={onChangeAppliedCoupon}
              isValueApplied={!!appliedCoupon}
              isLoading={isPlanLoading}
              appliedCouponValue={appliedCoupon}
            />
          )}

          <div className={styles.paymentContainer}>
            {enablePaypal && (
              <PayPalElements
                selectedPlan={plan}
                onPaymentProcessing={handlePaymentProcessingChange}
                numberOfSeats={numberOfSeats}
                onSdkReady={() => setReadyPaypalElements(true)}
              />
            )}

            {stripePromise && (
              <Elements
                stripe={stripePromise}
                options={{
                  mode: 'subscription',
                  appearance: {
                    theme: isDarkTheme ? 'night' : 'stripe',
                    variables: {
                      borderRadius: '12px',
                    },
                  },
                  currency: 'usd',
                  amount:
                    plan?.total_due_amount ||
                    (isTeams
                      ? (currentSelectedPlan?.total_due_amount || 1) *
                        numberOfSeats
                      : currentSelectedPlan?.total_due_amount),
                }}
              >
                <StripeElements
                  selectedPlan={plan}
                  onPaymentProcessing={handlePaymentProcessingChange}
                  numberOfSeats={numberOfSeats}
                  appliedCoupon={
                    !appliedCouponHasError ? appliedCoupon : undefined
                  }
                  onSdkReady={() => setReadyStripeElements(true)}
                />
              </Elements>
            )}
          </div>
        </div>
      </div>
    </AppearingFromRight>
  );
};
